import { Admin, Resource, Layout, AppBar } from 'react-admin';
import {authProvider, dataProvider} from '@services';
import { i18nProvider } from './i18n';
import { CustomMenu } from './components';

// Import Material-UI icons
import {
  People as UsersIcon,
  Photo as MediaIcon,
  Inventory as ProductsIcon,
  Storefront as BrandsIcon,
  Category as CategoriesIcon,
  LocalOffer as TagsIcon,
  Settings as AttributeGroupsIcon,
  Tune as AttributesIcon,
  AccountTree as CharacteristicGroupsIcon,
  Psychology as CharacteristicsIcon,
  Collections as CollectionsIcon,
  WorkOutline as VacancyCategoriesIcon,
  Work as VacanciesIcon,
  Description as VacancyPageDataIcon,
  Contacts as ContactGroupsIcon,
  Article as CasesIcon,
  Gavel as PrivacyIcon,
  AccountBalance as FinancingIcon,
  ContactMail as InquiriesIcon,
  Info as AboutIcon,
} from '@mui/icons-material';

// Import resources
import { UserList, UserEdit, UserCreate, UserShow } from './resources/users';
import { MediaList} from './resources/media';

// Import catalog resources
import { ProductList, ProductCreateTabs, ProductEditTabs, ProductShow } from './resources/products';
import { BrandList, BrandCreate, BrandEdit, BrandShow } from './resources/brands';
import { CategoryList, CategoryCreate, CategoryEdit, CategoryShow } from './resources/categories';
import { TagList, TagCreate, TagEdit, TagShow } from './resources/tags';
import { AttributeGroupList, AttributeGroupCreate, AttributeGroupEdit, AttributeGroupShow } from './resources/attribute-groups';
import { AttributeList, AttributeCreate, AttributeEdit, AttributeShow } from './resources/attributes';
import { CharacteristicGroupList, CharacteristicGroupCreate, CharacteristicGroupEdit, CharacteristicGroupShow } from './resources/characteristic-groups';
import { CharacteristicList, CharacteristicCreate, CharacteristicEdit, CharacteristicShow } from './resources/characteristics';
import { CollectionList, CollectionCreate, CollectionEdit, CollectionShow } from './resources/collections';

// Import vacancy resources
import { VacancyCategoryList, VacancyCategoryCreate, VacancyCategoryEdit, VacancyCategoryShow } from './resources/vacancy-categories';
import { VacancyList, VacancyCreate, VacancyEdit, VacancyShow } from './resources/vacancies';
import { VacancyPageDataList, VacancyPageDataCreate, VacancyPageDataEdit } from './resources/vacancy-page-data';

// Import contact resources
import { ContactGroupList, ContactGroupCreate, ContactGroupEdit, ContactGroupShow } from './resources/contact-groups';

// Import case resources
import { CaseCategoryList, CaseCategoryCreate, CaseCategoryEdit, CaseCategoryShow } from './resources/case-categories';
import { CaseList, CaseCreate, CaseEdit, CaseShow } from './resources/cases';

// Import privacy resources
import { PrivacySectionList, PrivacySectionCreate, PrivacySectionEdit, PrivacySectionShow } from './resources/privacy-sections';

// Import financing resources
import { FinancingList, FinancingCreate, FinancingEdit, FinancingShow } from './resources/financing';

// Import inquiry resources
import { InquiryList, InquiryShow, InquiryEdit } from './resources/inquiries';

// Import About resources
import { AboutList, AboutEdit, AboutCreate } from './resources/about';

// Custom layout components
const CustomAppBar = () => (
  <AppBar>
    <span style={{ flex: 1, fontSize: '1.2em', fontWeight: 'bold' }}>
      Панель управления Industrial
    </span>
  </AppBar>
);

const CustomLayout = (props: any) => (
  <Layout {...props} appBar={CustomAppBar} menu={CustomMenu} />
);

const App: React.FC = () => {
  return (
    <Admin
      dataProvider={dataProvider}
      authProvider={authProvider}
      i18nProvider={i18nProvider}
      layout={CustomLayout}
      title="Industrial Admin"
    >
      {/* Users Resource */}
      <Resource
        name="users"
        list={UserList}
        edit={UserEdit}
        create={UserCreate}
        show={UserShow}
        icon={UsersIcon}
        options={{ label: 'Пользователи' }}
      />

      {/* Media Resource */}
      <Resource
        name="media"
        list={MediaList}
        icon={MediaIcon}
        options={{ label: 'Медиа' }}
      />

      {/* Catalog Resources */}
      <Resource
        name="products"
        list={ProductList}
        create={ProductCreateTabs}
        edit={ProductEditTabs}
        show={ProductShow}
        icon={ProductsIcon}
        options={{ label: 'Товары' }}
      />

      <Resource
        name="brands"
        list={BrandList}
        create={BrandCreate}
        edit={BrandEdit}
        show={BrandShow}
        icon={BrandsIcon}
        options={{ label: 'Бренды' }}
      />

      <Resource
        name="categories"
        list={CategoryList}
        create={CategoryCreate}
        edit={CategoryEdit}
        show={CategoryShow}
        icon={CategoriesIcon}
        options={{ label: 'Категории' }}
      />

      <Resource
        name="tags"
        list={TagList}
        create={TagCreate}
        edit={TagEdit}
        show={TagShow}
        icon={TagsIcon}
        options={{ label: 'Теги' }}
      />

      <Resource
        name="attribute-groups"
        list={AttributeGroupList}
        create={AttributeGroupCreate}
        edit={AttributeGroupEdit}
        show={AttributeGroupShow}
        icon={AttributeGroupsIcon}
        options={{ label: 'Группы атрибутов' }}
      />

      <Resource
        name="attributes"
        list={AttributeList}
        create={AttributeCreate}
        edit={AttributeEdit}
        show={AttributeShow}
        icon={AttributesIcon}
        options={{ label: 'Атрибуты' }}
      />

      <Resource
        name="characteristic-groups"
        list={CharacteristicGroupList}
        create={CharacteristicGroupCreate}
        edit={CharacteristicGroupEdit}
        show={CharacteristicGroupShow}
        icon={CharacteristicGroupsIcon}
        options={{ label: 'Группы характеристик' }}
      />

      <Resource
        name="characteristics"
        list={CharacteristicList}
        create={CharacteristicCreate}
        edit={CharacteristicEdit}
        show={CharacteristicShow}
        icon={CharacteristicsIcon}
        options={{ label: 'Характеристики' }}
      />

      <Resource
        name="collections"
        list={CollectionList}
        create={CollectionCreate}
        edit={CollectionEdit}
        show={CollectionShow}
        icon={CollectionsIcon}
        options={{ label: 'Коллекции' }}
      />

      {/* Vacancy Resources */}
      <Resource
        name="vacancy-categories"
        list={VacancyCategoryList}
        create={VacancyCategoryCreate}
        edit={VacancyCategoryEdit}
        show={VacancyCategoryShow}
        icon={VacancyCategoriesIcon}
        options={{ label: 'Категории вакансий' }}
      />

      <Resource
        name="vacancies"
        list={VacancyList}
        create={VacancyCreate}
        edit={VacancyEdit}
        show={VacancyShow}
        icon={VacanciesIcon}
        options={{ label: 'Вакансии' }}
      />

      <Resource
        name="vacancy-page-data"
        list={VacancyPageDataList}
        create={VacancyPageDataCreate}
        edit={VacancyPageDataEdit}
        icon={VacancyPageDataIcon}
        options={{ label: 'Контент страницы' }}
      />

      {/* Contact Resources */}
      <Resource
        name="contact-groups"
        list={ContactGroupList}
        create={ContactGroupCreate}
        edit={ContactGroupEdit}
        show={ContactGroupShow}
        icon={ContactGroupsIcon}
        options={{ label: 'Группы контактов' }}
      />


      {/* Case Resources */}
      <Resource
        name="case-categories"
        list={CaseCategoryList}
        create={CaseCategoryCreate}
        edit={CaseCategoryEdit}
        show={CaseCategoryShow}
        icon={CategoriesIcon}
        options={{ label: 'Категории кейсов' }}
      />

      <Resource
        name="cases"
        list={CaseList}
        create={CaseCreate}
        edit={CaseEdit}
        show={CaseShow}
        icon={CasesIcon}
        options={{ label: 'Кейсы' }}
      />

      {/* Privacy Resources */}
      <Resource
        name="privacy-sections"
        list={PrivacySectionList}
        create={PrivacySectionCreate}
        edit={PrivacySectionEdit}
        show={PrivacySectionShow}
        icon={PrivacyIcon}
        options={{ label: 'Разделы политики' }}
      />


      {/* Financing Resource */}
      <Resource
        name="financing"
        list={FinancingList}
        create={FinancingCreate}
        edit={FinancingEdit}
        show={FinancingShow}
        icon={FinancingIcon}
        options={{ label: 'Финансирование' }}
      />

      {/* Inquiry Resource */}
      <Resource
        name="inquiries"
        list={InquiryList}
        show={InquiryShow}
        edit={InquiryEdit}
        icon={InquiriesIcon}
        options={{ label: 'Заявки' }}
      />

      {/* About Page Resource */}
      <Resource
        name="about"
        list={AboutList}
        edit={AboutEdit}
        create={AboutCreate}
        icon={AboutIcon}
        options={{ label: 'О нас' }}
      />
    </Admin>
  );
};

export default App;
